/**
 * Animations Orchestrator
 *
 * Conditionally loads animation modules based on DOM presence.
 * Uses registry pattern for scalable animation management.
 *
 * Features:
 * - Self-registering animation system (add new animations in one line)
 * - Dynamic imports for code-splitting
 * - Prevents duplicate initialization
 * - SPA-friendly rescan() method
 * - Centralized destroy cleanup
 * - Webflow IX3 coordination with timeout fallback
 *
 * Architecture:
 * - Registry-based discovery (scales to 100s of animations)
 * - Scans DOM for registered selectors
 * - Dynamically imports only the animations found
 * - Waits for Webflow IX3 before initializing (prevents FOUC)
 * - Removes transition wrapper on IX3 timeout (graceful degradation)
 * - Exposes rescan() for SPA page transitions
 *
 * Adding new animations:
 * 1. Add entry to ANIMATION_REGISTRY below
 * 2. That's it - orchestrator handles the rest
 */

import { createOrchestrator, type ModuleRegistryEntry } from '@utils';

/**
 * Animation Registry
 * Add new animations here - one line per animation
 */
const ANIMATION_REGISTRY: ModuleRegistryEntry[] = [
  {
    name: 'counter',
    selector: '[data-hs-anim="counter"]',
    import: () => import('./functions/counter.ts'),
  },
  {
    name: 'marquee',
    selector: '[data-hs-marquee="wrapper"]',
    import: () => import('./functions/marquee.ts'),
  },
  // Add more animations here:
  // { name: 'hamburger', selector: '[data-hs-hamburger="squeeze"]', import: () => import('./functions/hamburger.ts') },
];

// Pre-download animation modules as soon as this file loads (before init is called)
// This allows parallel downloads while still respecting IX3 timing for initialization
const preloadAnimations = (() => {
  const modulesToPreload = ANIMATION_REGISTRY.filter((module) =>
    document.querySelector(module.selector)
  );

  // Download all found modules in parallel
  const preloadPromises = modulesToPreload.map((module) =>
    module.import().catch((error) => {
      console.error(`[animations] Failed to preload ${module.name}:`, error);
      return null;
    })
  );

  return Promise.allSettled(preloadPromises);
})();

export async function init() {
  // Wait for preloading to complete (modules are already downloaded)
  await preloadAnimations;

  // Initialize all animations
  const orchestrator = createOrchestrator(ANIMATION_REGISTRY);
  const { loaded } = await orchestrator.scan();

  return {
    result: `animations initialized (${loaded.length} active)`,
    rescan: orchestrator.scan,
    destroy: orchestrator.destroy,
  };
}
